import React, { useState, useEffect } from 'react';
import { Card, Space, Row, Col, Button, Divider, Typography, Segmented, Tag } from 'antd';
import { debounce } from 'lodash';
import { Filter, Sliders } from 'lucide-react';

const { Text, Title } = Typography;

const NewsFilters = ({ publishers, symbols, onFilterChange, totalArticles }) => {
  const [publisherFilter, setPublisherFilter] = useState([]);
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [dateRange, setDateRange] = useState([0, 30]);
  const [symbolFilter, setSymbolFilter] = useState([]);
  const [showMorePublishers, setShowMorePublishers] = useState(false);
  const [showMoreSymbols, setShowMoreSymbols] = useState(false);

  // Debounced effect for updating the filters
  useEffect(() => {
    const debouncedFilter = debounce(() => {
      onFilterChange({
        publisher: publisherFilter,
        sentiment: sentimentFilter,
        dateRange,
        symbol: symbolFilter,
      });
    }, 300);
    debouncedFilter();

    return () => {
      debouncedFilter.cancel();
    };
  }, [publisherFilter, sentimentFilter, dateRange, symbolFilter]);

  const handlePublisherTagClick = (publisher) => {
    setPublisherFilter((prev) =>
      prev.includes(publisher)
        ? prev.filter((p) => p !== publisher) // Remove if already selected
        : [...prev, publisher] // Add if not selected
    );
  };

  const handleSymbolTagClick = (symbol) => {
    setSymbolFilter((prev) =>
      prev.includes(symbol)
        ? prev.filter((s) => s !== symbol) // Remove if already selected
        : [...prev, symbol] // Add if not selected
    );
  };

  const handleSentimentChange = (value) => {
    setSentimentFilter(value);
  };

  const TitleComponent = ({ Icon, title }) =>(
      <Row justify={'space-between'} align={'middle'} gutter={8} className={'mb-4'}>
        <Col>
            <span className={'component-title text-md font-medium text-gray-600'}>{title}</span>
        </Col>
          <Col>
          <Icon size={16} />
        </Col>
      </Row>
  )

  return (
    <Card className={'gapless py-3 pl-3 pr-6'} bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }}>

        {/* Publisher Filter */}
        <TitleComponent Icon={Filter} title={'Publishers'} />
        <div className="overflow-y-auto max-h-48 pr-2">
          <Space wrap>
            {(showMorePublishers ? Object.keys(publishers) : Object.keys(publishers).slice(0, 8)).map((publisher) => (
              <div
                key={publisher}
                className={`p-1 text-xs cursor-pointer transition-all duration-300 rounded-md
                ${publisherFilter.includes(publisher) ? 'bg-blue-500 text-white shadow-lg' : 'border border-blue-600 text-blue-600'}
                hover:shadow-md`}
                onClick={() => handlePublisherTagClick(publisher)}
                style={{ textAlign: 'center' }} // Adjust min-width based on longest publisher name
              >
                {publisher}
              </div>
            ))}
            {Object.keys(publishers).length > 8 && (
              <Button
                type="link"
                onClick={() => setShowMorePublishers((prev) => !prev)}
              >
                {showMorePublishers ? 'Show Less' : 'Show More'}
              </Button>
            )}
          </Space>
        </div>

        {/* Symbol Filter */}
        <Divider />
        <TitleComponent Icon={Filter} title={'Symbols'} />
        <div className="overflow-y-auto max-h-48 p-3 round-2">
          <Space wrap>
            {(showMoreSymbols ? symbols : symbols.slice(0, 20)).map((symbol) => (
              <Tag
                key={symbol}
                onClick={() => handleSymbolTagClick(symbol)}
                className="cursor-pointer transition-all duration-300 hover:shadow-md rounded-xl"
                style={{ userSelect: 'none', minWidth: '80px', textAlign: 'center', background: symbolFilter.includes(symbol) ? '#1890ff' : 'white', color: symbolFilter.includes(symbol) ? 'white' : 'black' }}
              >
                {symbol}
              </Tag>
            ))}
            {symbols.length > 20 && (
              <Button
                type="link"
                onClick={() => setShowMoreSymbols((prev) => !prev)}
              >
                {showMoreSymbols ? 'Show Less' : 'Show More'}
              </Button>
            )}
          </Space>
        </div>

        <Divider />
        <TitleComponent Icon={Sliders} title={'Sentiment'} />
        <Segmented
          options={[
            { label: 'Any', value: '' },
            { label: 'Positive', value: 'positive' },
            { label: 'Negative', value: 'negative' },
          ]}
          onChange={handleSentimentChange}
          block
        />

        {/* Show Total Articles Button */}
        <Divider />
        <Button
          type="primary"
          style={{ width: '100%' }}
          onClick={() =>
            onFilterChange({
              publisher: publisherFilter,
              sentiment: sentimentFilter,
              dateRange,
              symbol: symbolFilter,
            })
          }
        >
          Show {totalArticles} Articles
        </Button>
      </Space>
    </Card>
  );
};

export default NewsFilters;
